// SECURITY & AUDIT (spec §55, §114–§115): device encryption with a passphrase and a one-time recovery
// key, auto-lock, lock now, passphrase change; and the audit trail (who · what · when · previous · new),
// with restricted documents shown as field names only.
import { useEffect, useMemo, useState } from 'react';
import { CryptoAdapter, type CryptoMeta } from '@vero/store';
import { auditTrailCsv } from '@vero/domain';
import { download } from '../core/links.ts';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, Field, Modal, PageHead, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { VeroLogo } from '../core/brand.tsx';

type Ev = { target: HTMLInputElement & HTMLSelectElement };

const errText = (t: (k: string) => string, e: unknown) => { const c = (e as { code?: string }).code; return c ? t(`errors.${c}`) : t('common.error'); };
export function strength(p: string): 0 | 1 | 2 | 3 {
  let s = 0;
  if (p.length >= 10) s++;
  if (p.length >= 16) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p) || /\s/.test(p.trim())) s++;
  return Math.min(3, s) as 0 | 1 | 2 | 3;
}

export function SecurityView() {
  const rt = useRuntime();
  const { t } = rt;
  const encrypted = rt.engine.includes('aes-gcm');
  const inner = (rt.ws.adapter as CryptoAdapter).inner ?? rt.ws.adapter;
  const [meta, setMeta] = useState<CryptoMeta | null>(null);
  useEffect(() => { void CryptoAdapter.status(inner).then(setMeta).catch(() => setMeta(null)); }, [inner]);
  const supported = rt.engine.startsWith('indexeddb');
  return (
    <div data-testid="security">
      <PageHead title={t('security.title')} subtitle={t('security.subtitle')} actions={encrypted ? <button className="btn" onClick={() => location.reload()} data-testid="lock-now"><Icon name="lock" />{t('security.lock_now')}</button> : null} />
      <div className="grid grid-2" style={{ alignItems: 'start' }}>
        <section className="card card-pad">
          <div className="section-title">{t('security.encryption')}</div>
          {encrypted ? <>
            <div className="callout ok"><b>{t('security.on')}</b><div className="small">{t('security.on_detail', { iterations: meta?.kdf.iterations ?? 600000 })}</div></div>
            <AutoLock meta={meta} onChange={setMeta} />
            <div className="row wrap" style={{ gap: 8, marginTop: 10 }}><button className="btn" onClick={() => rt.open(<ChangePassphrase />)} data-testid="change-passphrase">{t('security.change')}</button></div>
          </> : supported ? <>
            <p className="small">{t('security.off_intro')}</p>
            <button className="btn btn-primary" onClick={() => rt.open(<EnableEncryption />)} data-testid="encrypt-enable"><Icon name="lock" />{t('security.enable')}</button>
          </> : <p className="callout small">{t(rt.engine.startsWith('memory') ? 'security.memory_note' : 'security.desktop_note')}</p>}
          <ul className="small muted" style={{ marginBottom: 0 }}>
            <li>{t('security.fact.keys')}</li><li>{t('security.fact.recovery')}</li><li>{t('security.fact.limits')}</li>
          </ul>
        </section>
        <AuditPanel />
      </div>
    </div>
  );
}

function AutoLock({ meta, onChange }: { meta: CryptoMeta | null; onChange: (m: CryptoMeta) => void }) {
  const rt = useRuntime();
  const inner = (rt.ws.adapter as CryptoAdapter).inner;
  const cur = String(meta?.autoLockMinutes ?? 15);
  return (
    <Field label={rt.t('security.autolock')} group>
      <Seg values={['5', '15', '30', '60', '0'] as const} value={cur as '15'} onChange={async (v) => { if (!meta) return; const next = { ...meta, autoLockMinutes: Number(v) }; await inner.setMeta('crypto', next); onChange(next); rt.toast(rt.t('common.saved'), 'success'); }} label={(v) => (v === '0' ? rt.t('security.never') : rt.t('security.minutes', { count: Number(v) }))} testId="autolock" />
    </Field>
  );
}

function EnableEncryption() {
  const rt = useRuntime();
  const { t } = rt;
  const [a, setA] = useState(''); const [b, setB] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const s = strength(a);
  const go = async () => {
    setBusy(true);
    try { const r = await CryptoAdapter.setup(rt.ws.adapter, a); setRecovery(r.recoveryKey); }
    catch (e) { rt.toast(errText(t, e), 'error'); }
    finally { setBusy(false); }
  };
  if (recovery) return (
    <Modal title={t('security.recovery_title')} footer={<button className="btn btn-primary" disabled={!ack} onClick={() => location.reload()} data-testid="recovery-done">{t('security.recovery_done')}</button>}>
      <p className="small">{t('security.recovery_intro')}</p>
      <pre className="recovery-key" data-testid="recovery-key">{recovery}</pre>
      <label className="check"><input type="checkbox" checked={ack} onChange={(e: Ev) => setAck(e.target.checked)} data-testid="recovery-ack" />{t('security.recovery_ack')}</label>
    </Modal>
  );
  return (
    <Modal title={t('security.enable')} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={busy || a.length < 10 || a !== b} onClick={() => void go()} data-testid="encrypt-submit">{busy ? t('security.encrypting') : t('security.enable')}</button></>}>
      <p className="small muted">{t('security.enable_hint')}</p>
      <div className="form-grid">
        <Field label={t('security.passphrase')} span2 hint={t(`security.strength.${s}`)}><input className="input" type="password" autoComplete="new-password" value={a} onChange={(e: Ev) => setA(e.target.value)} data-testid="pass-1" /></Field>
        <Field label={t('security.passphrase_again')} span2><input className="input" type="password" autoComplete="new-password" value={b} onChange={(e: Ev) => setB(e.target.value)} data-testid="pass-2" /></Field>
      </div>
      <div className={`meter lvl-${s}`} aria-hidden="true"><i style={{ width: `${Math.max(8, (s / 3) * 100)}%` }} /></div>
    </Modal>
  );
}

function ChangePassphrase() {
  const rt = useRuntime();
  const { t } = rt;
  const inner = (rt.ws.adapter as CryptoAdapter).inner;
  const [cur, setCur] = useState(''); const [next, setNext] = useState('');
  const go = async () => {
    try { await CryptoAdapter.changePassphrase(inner, cur.includes('-') && cur.length >= 29 ? { recoveryKey: cur } : { passphrase: cur }, next); rt.close(); rt.toast(t('security.changed'), 'success'); }
    catch (e) { rt.toast(errText(t, e), 'error'); }
  };
  return (
    <Modal title={t('security.change')} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={next.length < 10} onClick={() => void go()}>{t('common.save')}</button></>}>
      <div className="form-grid">
        <Field label={t('security.current_or_recovery')} span2><input className="input" type="password" value={cur} onChange={(e: Ev) => setCur(e.target.value)} /></Field>
        <Field label={t('security.new_passphrase')} span2 hint={t(`security.strength.${strength(next)}`)}><input className="input" type="password" autoComplete="new-password" value={next} onChange={(e: Ev) => setNext(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

/** The audit trail: who, what, when, previous value, new value (spec §115). */
function AuditPanel() {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const [q, setQ] = useState('');
  const exportCsv = () => download(`audit-trail-${rt.today}.csv`, '\ufeff' + auditTrailCsv(view.rows('audit_events'), { at: t('audit.col.at'), actor: t('audit.col.actor'), action: t('audit.col.action'), summary: t('audit.col.summary'), entity: t('audit.col.entity'), changes: t('audit.col.changes') }), 'text/csv;charset=utf-8');
  const events = useMemo(() => [...view.rows('audit_events')].sort((a, b) => b.at.localeCompare(a.at)).filter((e) => !q || `${e.action} ${e.summary ?? ''} ${e.entity ?? ''}`.toLowerCase().includes(q.toLowerCase())).slice(0, 80), [view, q]);
  return (
    <section className="card" data-testid="audit">
      <div className="card-head"><h3>{t('audit.title')}</h3><div className="row" style={{ gap: 8 }}><input className="input" style={{ maxWidth: 200 }} placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e: Ev) => setQ(e.target.value)} /><button className="btn btn-sm" onClick={exportCsv} data-testid="audit-export"><Icon name="download" />{t('audit.export')}</button></div></div>
      {events.length ? <div className="agenda-list">{events.map((e) => {
        const changes = ((e.data as { changes?: { t: string; id: string; f: Record<string, unknown> }[] } | undefined)?.changes ?? []).slice(0, 4);
        return (
          <div key={e.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr)' }} data-testid="audit-row">
            <div><b>{e.action}</b>{e.summary ? <span className="muted"> · {e.summary}</span> : null}<div className="tiny muted">{i18n.dateTime(e.at)} · {e.actor}{(e.data as { origin?: string } | undefined)?.origin === 'remote' ? ` · ${t('audit.synced')}` : ''}</div>
              {changes.map((c, i) => <div key={i} className="tiny">{t(`quality.table.${c.t}`)}: {Object.entries(c.f).slice(0, 6).map(([f, v]) => (Array.isArray(v) ? `${f} ${String(v[0] ?? '—')} → ${String(v[1] ?? '—')}` : `${f} ✎`)).join(' · ')}</div>)}
            </div>
          </div>);
      })}</div> : <Empty icon="records">{t('common.empty')}</Empty>}
      <p className="tiny muted" style={{ padding: '0 20px 14px' }}>{t('audit.note')}</p>
    </section>
  );
}

/** Lock screen shown at boot when the device store is encrypted. */
export function LockScreen({ onUnlock, locale }: { onUnlock: (secret: { passphrase: string } | { recoveryKey: string }) => Promise<void>; locale: { t: (k: string, p?: Record<string, string | number>) => string } }) {
  const { t } = locale;
  const [mode, setMode] = useState<'passphrase' | 'recovery'>('passphrase');
  const [v, setV] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); setErr(null); try { await onUnlock(mode === 'passphrase' ? { passphrase: v } : { recoveryKey: v }); } catch (e) { setErr(t(`errors.${(e as { code?: string }).code ?? 'crypto.wrong_passphrase'}`)); setBusy(false); } };
  return (
    <div className="boot" data-testid="lock-screen">
      <form className="card card-pad" style={{ maxWidth: 420, width: '100%', textAlign: 'center', padding: 32 }} onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); void go(); }}>
        <div style={{ display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}><VeroLogo width={112} /></div>
        <h1 style={{ margin: '0 0 6px', fontSize: 22 }}>{t('lock.title')}</h1>
        <p className="muted small" style={{ marginTop: 0 }}>{t('lock.subtitle')}</p>
        <input className="input" type="password" autoFocus autoComplete="current-password" placeholder={t(mode === 'passphrase' ? 'security.passphrase' : 'lock.recovery_key')} value={v} onChange={(e: Ev) => setV(e.target.value)} data-testid="unlock-input" style={{ marginTop: 10 }} />
        {err && <p className="small" style={{ color: 'var(--crit)' }} data-testid="unlock-error">{err}</p>}
        <button className="btn btn-primary" type="submit" disabled={busy || !v} style={{ width: '100%', marginTop: 12 }} data-testid="unlock-submit">{busy ? t('lock.unlocking') : t('lock.unlock')}</button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => { setMode(mode === 'passphrase' ? 'recovery' : 'passphrase'); setV(''); }} style={{ marginTop: 8 }}>{t(mode === 'passphrase' ? 'lock.use_recovery' : 'lock.use_passphrase')}</button>
      </form>
    </div>
  );
}
